import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const QuickActionFloatingMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [contextActions, setContextActions] = useState([]);
  const location = useLocation();

  // Define context-specific actions based on current route
  useEffect(() => {
    const getContextActions = () => {
      switch (location.pathname) {
        case '/dashboard':
          return [
            {
              id: 'generate-chart',
              label: 'Generate Chart',
              icon: 'PieChart',
              color: 'primary',
              action: () => console.log('Generate astrological chart')
            },
            {
              id: 'quick-reading',
              label: 'Quick Reading',
              icon: 'Eye',
              color: 'secondary',
              action: () => console.log('Quick astrological reading')
            },
            {
              id: 'save-moment',
              label: 'Save Moment',
              icon: 'Bookmark',
              color: 'accent',
              action: () => console.log('Save current astrological moment')
            }
          ];
        
        case '/astrological-audio-player':
          return [
            {
              id: 'export-audio',
              label: 'Export Audio',
              icon: 'Download',
              color: 'primary',
              action: () => console.log('Export current audio')
            },
            {
              id: 'create-playlist',
              label: 'Create Playlist',
              icon: 'ListMusic',
              color: 'secondary',
              action: () => console.log('Create new playlist')
            },
            {
              id: 'share-track',
              label: 'Share Track',
              icon: 'Share',
              color: 'accent',
              action: () => console.log('Share current track')
            },
            {
              id: 'record-session',
              label: 'Record Session',
              icon: 'Mic',
              color: 'error',
              action: () => console.log('Record audio session')
            }
          ];
        
        case '/meet-someone-new':
          return [
            {
              id: 'send-invite',
              label: 'Send Invite',
              icon: 'UserPlus',
              color: 'primary',
              action: () => console.log('Send friend invitation')
            },
            {
              id: 'compatibility-check',
              label: 'Check Compatibility',
              icon: 'Heart',
              color: 'error',
              action: () => console.log('Check compatibility')
            },
            {
              id: 'nearby-souls',
              label: 'Find Nearby',
              icon: 'MapPin',
              color: 'accent',
              action: () => console.log('Find nearby compatible souls')
            }
          ];
        
        default:
          return [];
      }
    };

    setContextActions(getContextActions());
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action) => {
    action.action();
    setIsOpen(false);
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'primary':
        return 'bg-primary hover:bg-primary/80 text-primary-foreground';
      case 'secondary':
        return 'bg-secondary hover:bg-secondary/80 text-secondary-foreground';
      case 'accent':
        return 'bg-accent hover:bg-accent/80 text-accent-foreground';
      case 'error':
        return 'bg-error hover:bg-error/80 text-error-foreground';
      default:
        return 'bg-surface hover:bg-surface/80 text-text-primary';
    }
  };

  if (contextActions.length === 0) return null;

  return (
    <div className="fixed bottom-20 right-6 z-dropdown">
      {/* Action Items */}
      <div className={`mb-4 space-y-3 transition-all duration-300 ${
        isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}>
        {contextActions.map((action, index) => (
          <div
            key={action.id}
            className={`transform transition-all duration-300 ${
              isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
            style={{ transitionDelay: `${index * 50}ms` }}
          >
            <button
              onClick={() => handleActionClick(action)}
              className={`
                flex items-center space-x-3 px-4 py-3 rounded-full shadow-elevation-3
                transition-cosmic glass backdrop-blur-cosmic
                ${getColorClasses(action.color)}
                hover:shadow-elevation-4 hover:scale-105
                group
              `}
            >
              <Icon name={action.icon} size={20} color="currentColor" />
              <span className="text-sm font-medium whitespace-nowrap">
                {action.label}
              </span>
            </button>
          </div>
        ))}
      </div>

      {/* Main Toggle Button */}
      <button
        onClick={toggleMenu}
        className={`
          w-14 h-14 rounded-full shadow-elevation-4 transition-all duration-300
          glass bg-primary/90 backdrop-blur-cosmic text-primary-foreground
          hover:shadow-elevation-5 hover:scale-110 active:scale-95
          flex items-center justify-center group
          ${isOpen ? 'rotate-45' : 'rotate-0'}
        `}
      >
        <Icon 
          name={isOpen ? "X" : "Plus"} 
          size={24} 
          color="white"
          className="transition-transform duration-300"
        />
        
        {/* Cosmic glow effect */}
        <div className="absolute inset-0 rounded-full bg-primary/30 animate-cosmic-glow"></div>
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-background/20 backdrop-blur-sm z-dropdown-backdrop"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default QuickActionFloatingMenu;